/**
 * useFriendCall Hook
 *
 * Wrapper around useP2PVoiceCall for friend-to-friend calling.
 * Handles incoming calls, outgoing calls, and call state management.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { RealtimeChannel } from '@supabase/supabase-js';

export type CallState = 'idle' | 'calling' | 'ringing' | 'connected' | 'ending';
export type CallType = 'voice' | 'video';

export interface CallParticipant {
  id: string;
  username: string;
  avatar_url: string | null;
  isMuted: boolean;
  isDeafened: boolean;
  isVideoOn: boolean;
}

export interface ActiveCall {
  id: string;
  callerId: string;
  callerName: string;
  callerAvatar: string | null;
  receiverId: string;
  receiverName: string;
  receiverAvatar: string | null;
  type: CallType;
  state: CallState;
  startTime: string | null;
  endTime: string | null;
}

export interface IncomingCall {
  callId: string;
  callerId: string;
  callerName: string;
  callerAvatar: string | null;
  type: CallType;
}

interface UseFriendCallReturn {
  // State
  callState: CallState;
  activeCall: ActiveCall | null;
  incomingCall: IncomingCall | null;
  isMuted: boolean;
  isDeafened: boolean;
  isVideoOn: boolean;
  localStream: MediaStream | null;
  remoteStream: MediaStream | null;
  callDuration: number;
  error: string | null;

  // Actions
  startCall: (friendId: string, friendName: string, friendAvatar: string | null, type?: CallType) => Promise<void>;
  acceptCall: () => Promise<void>;
  rejectCall: () => Promise<void>;
  endCall: () => Promise<void>;
  toggleMute: () => void;
  toggleDeafen: () => void;
  toggleVideo: () => void;
}

// Store call records in Supabase for signaling
// Note: In production, use a proper signaling server (WebSocket, Firebase, etc.)
// For now, we use Supabase Realtime for call signaling

export function useFriendCall(userId: string | null, username: string, userAvatar: string | null): UseFriendCallReturn {
  const [callState, setCallState] = useState<CallState>('idle');
  const [activeCall, setActiveCall] = useState<ActiveCall | null>(null);
  const [incomingCall, setIncomingCall] = useState<IncomingCall | null>(null);
  const [isMuted, setIsMuted] = useState(false);
  const [isDeafened, setIsDeafened] = useState(false);
  const [isVideoOn, setIsVideoOn] = useState(false);
  const [localStream, setLocalStream] = useState<MediaStream | null>(null);
  const [remoteStream, setRemoteStream] = useState<MediaStream | null>(null);
  const [callDuration, setCallDuration] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const peerConnectionRef = useRef<RTCPeerConnection | null>(null);
  const signalingChannelRef = useRef<RealtimeChannel | null>(null);
  const callTimerRef = useRef<NodeJS.Timeout | null>(null);

  // ICE servers configuration (using free STUN servers)
  const iceServers = {
    iceServers: [
      { urls: 'stun:stun.l.google.com:19302' },
      { urls: 'stun:stun1.l.google.com:19302' },
    ],
  };

  // Setup signaling channel for incoming calls
  useEffect(() => {
    if (!userId) return;

    // Subscribe to call signals directed at this user
    const channel = supabase
      .channel(`call_signals:${userId}`)
      .on('broadcast', { event: 'incoming_call' }, (payload) => {
        const { callId, callerId, callerName, callerAvatar, callType } = payload.payload;
        setIncomingCall({
          callId,
          callerId,
          callerName,
          callerAvatar,
          type: callType,
        });
        setCallState('ringing');
      })
      .on('broadcast', { event: 'call_accepted' }, (payload) => {
        // Remote user accepted our call
        handleCallAccepted(payload.payload);
      })
      .on('broadcast', { event: 'call_rejected' }, () => {
        // Remote user rejected our call
        cleanup();
        setCallState('idle');
      })
      .on('broadcast', { event: 'call_ended' }, () => {
        // Remote user ended the call
        cleanup();
        setCallState('idle');
      })
      .on('broadcast', { event: 'ice_candidate' }, (payload) => {
        handleIceCandidate(payload.payload.candidate);
      })
      .on('broadcast', { event: 'sdp_answer' }, (payload) => {
        handleSdpAnswer(payload.payload.answer);
      })
      .on('broadcast', { event: 'sdp_offer' }, (payload) => {
        handleSdpOffer(payload.payload.offer);
      })
      .subscribe();

    signalingChannelRef.current = channel;

    return () => {
      channel.unsubscribe();
    };
  }, [userId]);

  // Cleanup function
  const cleanup = useCallback(() => {
    // Stop local stream
    if (localStream) {
      localStream.getTracks().forEach(track => track.stop());
      setLocalStream(null);
    }

    // Close peer connection
    if (peerConnectionRef.current) {
      peerConnectionRef.current.close();
      peerConnectionRef.current = null;
    }

    // Clear call timer
    if (callTimerRef.current) {
      clearInterval(callTimerRef.current);
      callTimerRef.current = null;
    }

    setRemoteStream(null);
    setActiveCall(null);
    setIncomingCall(null);
    setCallDuration(0);
    setError(null);
  }, [localStream]);

  // Get user media
  const getUserMedia = async (video: boolean = false): Promise<MediaStream> => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: video,
      });
      return stream;
    } catch (err) {
      console.error('Failed to get user media:', err);
      throw new Error('Could not access microphone' + (video ? '/camera' : ''));
    }
  };

  // Helper to send signal to a target user (properly subscribes first)
  const sendSignal = useCallback(async (targetUserId: string, event: string, payload: any) => {
    const channelName = `call_signals:${targetUserId}`;
    const channel = supabase.channel(channelName);

    return new Promise<void>((resolve, reject) => {
      channel
        .subscribe((status) => {
          if (status === 'SUBSCRIBED') {
            channel.send({
              type: 'broadcast',
              event,
              payload,
            }).then(() => {
              // Unsubscribe after sending (we have our own listener)
              setTimeout(() => channel.unsubscribe(), 100);
              resolve();
            }).catch(reject);
          } else if (status === 'CHANNEL_ERROR') {
            reject(new Error('Channel error'));
          }
        });
    });
  }, []);

  // Create peer connection
  const createPeerConnection = useCallback((targetUserId: string) => {
    const pc = new RTCPeerConnection(iceServers);

    pc.onicecandidate = (event) => {
      if (event.candidate) {
        // Send ICE candidate to remote peer
        sendSignal(targetUserId, 'ice_candidate', { candidate: event.candidate });
      }
    };

    pc.ontrack = (event) => {
      setRemoteStream(event.streams[0]);
    };

    pc.onconnectionstatechange = () => {
      console.log('Connection state:', pc.connectionState);
      if (pc.connectionState === 'connected') {
        setCallState('connected');
        // Start call timer
        const startTime = Date.now();
        callTimerRef.current = setInterval(() => {
          setCallDuration(Math.floor((Date.now() - startTime) / 1000));
        }, 1000);
      } else if (pc.connectionState === 'disconnected' || pc.connectionState === 'failed') {
        cleanup();
        setCallState('idle');
      }
    };

    return pc;
  }, [cleanup, sendSignal]);

  // Handle received SDP offer (for call receiver)
  const handleSdpOffer = async (offer: RTCSessionDescriptionInit) => {
    if (!peerConnectionRef.current || !incomingCall) return;

    try {
      await peerConnectionRef.current.setRemoteDescription(offer);
      const answer = await peerConnectionRef.current.createAnswer();
      await peerConnectionRef.current.setLocalDescription(answer);

      // Send answer to caller
      await sendSignal(incomingCall.callerId, 'sdp_answer', { answer });
    } catch (err) {
      console.error('Failed to handle SDP offer:', err);
      setError('Failed to establish call');
    }
  };

  // Handle received SDP answer (for call initiator)
  const handleSdpAnswer = async (answer: RTCSessionDescriptionInit) => {
    if (!peerConnectionRef.current) return;

    try {
      await peerConnectionRef.current.setRemoteDescription(answer);
    } catch (err) {
      console.error('Failed to handle SDP answer:', err);
      setError('Failed to establish call');
    }
  };

  // Handle received ICE candidate
  const handleIceCandidate = async (candidate: RTCIceCandidateInit) => {
    if (!peerConnectionRef.current) return;

    try {
      await peerConnectionRef.current.addIceCandidate(candidate);
    } catch (err) {
      console.error('Failed to add ICE candidate:', err);
    }
  };

  // Handle call accepted by remote user
  const handleCallAccepted = async (payload: any) => {
    // The remote user accepted, now we wait for their SDP answer
    setCallState('connected');
  };

  // Start a call
  const startCall = useCallback(async (
    friendId: string,
    friendName: string,
    friendAvatar: string | null,
    type: CallType = 'voice'
  ) => {
    if (!userId) return;

    try {
      setError(null);
      setCallState('calling');

      // Get local media
      const stream = await getUserMedia(type === 'video');
      setLocalStream(stream);
      setIsVideoOn(type === 'video');

      // Create peer connection
      const pc = createPeerConnection(friendId);
      peerConnectionRef.current = pc;

      // Add local tracks to peer connection
      stream.getTracks().forEach(track => {
        pc.addTrack(track, stream);
      });

      // Create offer
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      // Set active call
      const callId = `${userId}-${friendId}-${Date.now()}`;
      setActiveCall({
        id: callId,
        callerId: userId,
        callerName: username,
        callerAvatar: userAvatar,
        receiverId: friendId,
        receiverName: friendName,
        receiverAvatar: friendAvatar,
        type,
        state: 'calling',
        startTime: null,
        endTime: null,
      });

      // Signal the friend about incoming call
      await sendSignal(friendId, 'incoming_call', {
        callId,
        callerId: userId,
        callerName: username,
        callerAvatar: userAvatar,
        callType: type,
      });

      // Send SDP offer
      await sendSignal(friendId, 'sdp_offer', { offer });

    } catch (err: any) {
      console.error('Failed to start call:', err);
      setError(err.message || 'Failed to start call');
      cleanup();
      setCallState('idle');
    }
  }, [userId, username, userAvatar, createPeerConnection, cleanup, sendSignal]);

  // Accept incoming call
  const acceptCall = useCallback(async () => {
    if (!incomingCall || !userId) return;

    try {
      setError(null);

      // Get local media
      const stream = await getUserMedia(incomingCall.type === 'video');
      setLocalStream(stream);
      setIsVideoOn(incomingCall.type === 'video');

      // Create peer connection
      const pc = createPeerConnection(incomingCall.callerId);
      peerConnectionRef.current = pc;

      // Add local tracks to peer connection
      stream.getTracks().forEach(track => {
        pc.addTrack(track, stream);
      });

      // Set active call
      setActiveCall({
        id: incomingCall.callId,
        callerId: incomingCall.callerId,
        callerName: incomingCall.callerName,
        callerAvatar: incomingCall.callerAvatar,
        receiverId: userId,
        receiverName: username,
        receiverAvatar: userAvatar,
        type: incomingCall.type,
        state: 'connected',
        startTime: new Date().toISOString(),
        endTime: null,
      });

      // Notify caller that call was accepted
      await sendSignal(incomingCall.callerId, 'call_accepted', { callId: incomingCall.callId });

      setIncomingCall(null);
      setCallState('connected');

    } catch (err: any) {
      console.error('Failed to accept call:', err);
      setError(err.message || 'Failed to accept call');
      cleanup();
      setCallState('idle');
    }
  }, [incomingCall, userId, username, userAvatar, createPeerConnection, cleanup, sendSignal]);

  // Reject incoming call
  const rejectCall = useCallback(async () => {
    if (!incomingCall) return;

    // Notify caller that call was rejected
    await sendSignal(incomingCall.callerId, 'call_rejected', { callId: incomingCall.callId });

    setIncomingCall(null);
    setCallState('idle');
  }, [incomingCall, sendSignal]);

  // End call
  const endCall = useCallback(async () => {
    const targetId = activeCall
      ? (activeCall.callerId === userId ? activeCall.receiverId : activeCall.callerId)
      : incomingCall?.callerId;

    if (targetId) {
      await sendSignal(targetId, 'call_ended', {});
    }

    cleanup();
    setCallState('idle');
  }, [activeCall, incomingCall, userId, cleanup, sendSignal]);

  // Toggle mute
  const toggleMute = useCallback(() => {
    if (localStream) {
      const audioTrack = localStream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !audioTrack.enabled;
        setIsMuted(!audioTrack.enabled);
      }
    }
  }, [localStream]);

  // Toggle deafen
  const toggleDeafen = useCallback(() => {
    if (remoteStream) {
      remoteStream.getAudioTracks().forEach(track => {
        track.enabled = isDeafened;
      });
      setIsDeafened(!isDeafened);
    }
  }, [remoteStream, isDeafened]);

  // Toggle video
  const toggleVideo = useCallback(() => {
    if (localStream) {
      const videoTrack = localStream.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !videoTrack.enabled;
        setIsVideoOn(videoTrack.enabled);
      }
    }
  }, [localStream]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, [cleanup]);

  return {
    callState,
    activeCall,
    incomingCall,
    isMuted,
    isDeafened,
    isVideoOn,
    localStream,
    remoteStream,
    callDuration,
    error,
    startCall,
    acceptCall,
    rejectCall,
    endCall,
    toggleMute,
    toggleDeafen,
    toggleVideo,
  };
}
